import React, { useEffect, useRef, useState } from 'react';
import './Starfield.css';

const staticSkyQuery = '(prefers-reduced-motion: reduce), (forced-colors: active)';

// A repeatable sky keeps stars in place when motion preferences change.
const makeStars = count => {
  let seed = 310;
  const random = () => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };
  return Array.from({ length: count }, () => ({
    x: random(), y: random(), depth: .2 + random() * .8,
    radius: .4 + random() * .9,
    opacity: .2 + random() * .4,
    phase: random() * Math.PI * 2,
    speed: .25 + random() * .35,
    violet: random() > .74,
  }));
};

// Build short, stable chains once per resize so connections don't jump as the
// pointer moves. Each star has at most two neighbors, with no closed triangles.
const makeConstellations = (stars, width, height) => {
  const candidates = [];
  const degree = stars.map(() => 0);
  const groups = stars.map((_, index) => index);
  const group = index => {
    while (groups[index] !== index) index = groups[index];
    return index;
  };
  stars.forEach((star, a) => {
    for (let b = a + 1; b < stars.length; b++) {
      const distance = Math.hypot((star.x - stars[b].x) * width, (star.y - stars[b].y) * height);
      if (distance > 24 && distance < 160) candidates.push({ a, b, distance });
    }
  });
  return candidates.sort((a, b) => a.distance - b.distance).filter(({ a, b }) => {
    const first = group(a);
    const second = group(b);
    if (degree[a] >= 2 || degree[b] >= 2 || first === second) return false;
    degree[a]++;
    degree[b]++;
    groups[first] = second;
    return true;
  });
};

export const Starfield = () => {
  const canvasRef = useRef(null);
  const scene = useRef({ time: 0, x: 0, y: 0, meteor: null, nextMeteor: 9 });
  const [staticSky, setStaticSky] = useState(() => window.matchMedia?.(staticSkyQuery).matches ?? false);

  useEffect(() => {
    const preference = window.matchMedia?.(staticSkyQuery);
    const update = () => setStaticSky(preference.matches);
    preference?.addEventListener('change', update);
    return () => preference?.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !window.CanvasRenderingContext2D) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    const sky = scene.current;
    const moving = !staticSky;
    const pointerQuery = window.matchMedia?.('(hover: hover) and (pointer: fine)');
    let width = 0;
    let height = 0;
    let stars = [];
    let constellations = [];
    let frame = 0;
    let lastPaint = 0;
    let targetX = sky.x;
    let targetY = sky.y;
    let sparkles = [];
    let emissionBudget = 5;
    const hover = {
      active: false, x: 0, y: 0, lastX: 0, lastY: 0,
      glowX: 0, glowY: 0, strength: 0, speed: 0, lastMotion: 0,
    };

    // Reuse one soft light texture for halos instead of blurring every particle.
    const bloom = document.createElement('canvas');
    bloom.width = bloom.height = 64;
    const bloomContext = bloom.getContext('2d');
    if (!bloomContext) return;
    const bloomGradient = bloomContext.createRadialGradient(32, 32, 0, 32, 32, 32);
    bloomGradient.addColorStop(0, 'rgba(211,191,255,1)');
    bloomGradient.addColorStop(.18, 'rgba(189,162,251,.55)');
    bloomGradient.addColorStop(.5, 'rgba(139,115,210,.16)');
    bloomGradient.addColorStop(1, 'rgba(139,115,210,0)');
    bloomContext.fillStyle = bloomGradient;
    bloomContext.fillRect(0, 0, 64, 64);
    const drawBloom = (x, y, radius, alpha) => {
      context.globalAlpha = alpha;
      context.drawImage(bloom, x - radius, y - radius, radius * 2, radius * 2);
      context.globalAlpha = 1;
    };

    const updateSparkles = delta => {
      sparkles = sparkles.filter(sparkle => sky.time - sparkle.start < sparkle.life);
      emissionBudget = Math.min(5, emissionBudget + delta * 28);
      const ease = 1 - Math.exp(-delta * 8);
      // Let the background settle while someone pauses to read.
      const idle = Math.max(0, sky.time - hover.lastMotion - .25);
      const light = hover.active ? Math.exp(-idle * 2) : 0;
      hover.strength += (light - hover.strength) * ease;
      hover.glowX += (hover.x - hover.glowX) * ease;
      hover.glowY += (hover.y - hover.glowY) * ease;
      const dx = hover.x - hover.lastX;
      const dy = hover.y - hover.lastY;
      const distance = Math.hypot(dx, dy);
      const speed = Math.min(distance / Math.max(delta, .001), 1200);
      hover.speed += ((hover.active ? speed : 0) - hover.speed) * ease;
      hover.lastX = hover.x;
      hover.lastY = hover.y;
      if (!hover.active || distance < 2) return;

      // Limit arrivals, letting every existing particle finish its fade.
      const count = Math.min(Math.floor(emissionBudget), 72 - sparkles.length, Math.max(1, Math.ceil(distance / 9)));
      emissionBudget -= count;
      // Emit on animation frames, not on every event from a high-polling mouse.
      // Keep a fast pointer jump from scattering particles across the page.
      const trail = distance ? Math.min(distance, 70) / distance : 0;
      for (let i = 0; i < count; i++) {
        const behind = (1 - (i + 1) / count) * trail;
        const dust = Math.random() < .72;
        const spread = 8 + hover.speed * .012;
        sparkles.push({
          x: hover.x - dx * behind + (Math.random() - .5) * spread,
          y: hover.y - dy * behind + (Math.random() - .5) * spread,
          vx: (distance ? dx / distance * speed * .018 : 0) + (Math.random() - .5) * 12,
          vy: (distance ? dy / distance * speed * .018 : 0) + 3 + Math.random() * 9,
          size: dust ? .55 + Math.random() : 2.2 + Math.random() * 2.4,
          rotation: Math.random() * Math.PI / 3,
          life: .85 + Math.random() * .8,
          start: sky.time,
          dust,
          blue: Math.random() < .3,
        });
      }
    };

    const draw = () => {
      context.clearRect(0, 0, width, height);
      if (moving && hover.strength > .01) {
        drawBloom(hover.glowX, hover.glowY, 230 + hover.speed * .025, hover.strength * .1);
        drawBloom(hover.x, hover.y, 65, hover.strength * .035);
      }
      for (const star of stars) {
        star.px = star.x * width + (sky.x * 11 + Math.sin(sky.time * .035) * 6) * star.depth;
        star.py = star.y * height + (sky.y * 8 + Math.cos(sky.time * .025) * 4) * star.depth;
        star.proximity = moving ? Math.max(0, 1 - Math.hypot(star.px - hover.glowX, star.py - hover.glowY) / 210) * hover.strength : 0;
      }
      if (moving && hover.strength > .01) {
        context.lineWidth = .7;
        for (const { a, b, distance } of constellations) {
          const first = stars[a];
          const second = stars[b];
          const reveal = Math.min(first.proximity, second.proximity);
          if (reveal < .025) continue;
          context.strokeStyle = `rgba(189,174,232,${reveal * .32 * (1 - distance / 240)})`;
          context.beginPath();
          context.moveTo(first.px, first.py);
          context.lineTo(second.px, second.py);
          context.stroke();
        }
      }
      for (const star of stars) {
        const { px: x, py: y, proximity } = star;
        // Less light through the reading area, more detail around the edges.
        const edge = .58 + Math.abs(star.x - .5) * .84;
        const alpha = Math.min(.95, (star.opacity + Math.sin(sky.time * star.speed + star.phase) * .12) * edge + proximity * .36);
        if (proximity > .08) drawBloom(x, y, 9 + proximity * 10, proximity * .22);
        context.fillStyle = `rgba(${star.violet ? '197,175,244' : '222,229,245'},${alpha})`;
        context.beginPath();
        context.arc(x, y, star.radius + proximity * .55, 0, Math.PI * 2);
        context.fill();
        if (star.radius > 1.23 || proximity > .55) {
          const ray = 3 + proximity * 3;
          context.fillStyle = `rgba(213,199,245,${alpha * (.18 + proximity * .25)})`;
          context.fillRect(x - ray, y - .4, ray * 2, .8);
          context.fillRect(x - .4, y - ray, .8, ray * 2);
        }
      }

      for (const sparkle of sparkles) {
        const age = sky.time - sparkle.start;
        const remaining = Math.max(0, 1 - age / sparkle.life);
        const alpha = Math.min(1, age / .08) * remaining * remaining;
        const size = sparkle.size * (.45 + remaining * .55);
        const x = sparkle.x + sparkle.vx * age;
        const y = sparkle.y + sparkle.vy * age;
        if (sparkle.dust) {
          context.fillStyle = `rgba(${sparkle.blue ? '172,210,241' : '219,204,253'},${alpha * .7})`;
          context.beginPath();
          context.arc(x, y, size, 0, Math.PI * 2);
          context.fill();
          continue;
        }
        drawBloom(x, y, size * 4, alpha * .2);
        context.save();
        context.translate(x, y);
        context.rotate(sparkle.rotation + age * .3);
        context.fillStyle = `rgba(233,222,255,${alpha * .9})`;
        context.beginPath();
        context.moveTo(0, -size);
        context.quadraticCurveTo(size * .18, -size * .18, size, 0);
        context.quadraticCurveTo(size * .18, size * .18, 0, size);
        context.quadraticCurveTo(-size * .18, size * .18, -size, 0);
        context.quadraticCurveTo(-size * .18, -size * .18, 0, -size);
        context.fill();
        context.restore();
      }

      if (sky.meteor && sky.time - sky.meteor.start >= 1.35) sky.meteor = null;
      if (moving && sky.meteor) {
        const age = sky.time - sky.meteor.start;
        const progress = age / 1.35;
        const travel = Math.min(width * .34, 380);
        const x = sky.meteor.x * width - progress * travel;
        const y = sky.meteor.y * height + progress * travel * .5;
        const tail = Math.min(width * .18, 135);
        const glow = Math.sin(progress * Math.PI) * .48;
        const gradient = context.createLinearGradient(x, y, x + tail, y - tail * .5);
        gradient.addColorStop(0, `rgba(230,218,255,${glow})`);
        gradient.addColorStop(1, 'rgba(189,162,251,0)');
        context.strokeStyle = gradient;
        context.lineWidth = 1.2;
        context.beginPath();
        context.moveTo(x, y);
        context.lineTo(x + tail, y - tail * .5);
        context.stroke();
      }
    };

    const resize = () => {
      width = document.documentElement.clientWidth;
      height = window.innerHeight;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      stars = makeStars(Math.min(220, Math.max(55, Math.round(width * height / 7500))));
      constellations = makeConstellations(stars, width, height);
      draw();
    };

    const animate = now => {
      if (now - lastPaint >= 1000 / 30) {
        const delta = Math.min((now - lastPaint) / 1000, .05);
        lastPaint = now;
        sky.time += delta;
        sky.x += (targetX - sky.x) * .045;
        sky.y += (targetY - sky.y) * .045;
        updateSparkles(delta);
        if (sky.time >= sky.nextMeteor) {
          sky.meteor = { start: sky.time, x: .5 + Math.random() * .45, y: .06 + Math.random() * .3 };
          sky.nextMeteor = sky.time + 22 + Math.random() * 16;
        }
        draw();
      }
      frame = requestAnimationFrame(animate);
    };

    const syncVisibility = () => {
      cancelAnimationFrame(frame);
      resetPointer();
      sparkles = [];
      hover.strength = 0;
      if (moving && !document.hidden) {
        lastPaint = performance.now();
        frame = requestAnimationFrame(animate);
      }
    };
    const movePointer = event => {
      if (!pointerQuery?.matches || event.pointerType === 'touch' || !document.hasFocus()) {
        resetPointer();
        return;
      }
      targetX = event.clientX / width * 2 - 1;
      targetY = event.clientY / height * 2 - 1;
      if (!hover.active) {
        hover.lastX = event.clientX;
        hover.lastY = event.clientY;
        hover.glowX = event.clientX;
        hover.glowY = event.clientY;
        hover.strength = 0;
        hover.speed = 0;
      }
      if (!hover.active || event.clientX !== hover.x || event.clientY !== hover.y) hover.lastMotion = sky.time;
      hover.active = true;
      hover.x = event.clientX;
      hover.y = event.clientY;
    };
    const resetPointer = () => {
      targetX = 0;
      targetY = 0;
      hover.active = false;
      hover.speed = 0;
      hover.lastX = hover.x;
      hover.lastY = hover.y;
    };

    if (!moving) sky.meteor = null;
    resize();
    syncVisibility();
    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', syncVisibility);
    if (moving) {
      window.addEventListener('pointermove', movePointer, { passive: true });
      document.documentElement.addEventListener('pointerleave', resetPointer);
      window.addEventListener('blur', resetPointer);
      pointerQuery?.addEventListener('change', resetPointer);
    }
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', syncVisibility);
      window.removeEventListener('pointermove', movePointer);
      document.documentElement.removeEventListener('pointerleave', resetPointer);
      window.removeEventListener('blur', resetPointer);
      pointerQuery?.removeEventListener('change', resetPointer);
    };
  }, [staticSky]);

  return (
    <div className="starfield" aria-hidden="true"><canvas ref={canvasRef} className="starfield-canvas" /></div>
  );
};
